<template>
    <div
        class="billing-plans"
        :class="{loading: !plans}"
        >
          <i
            v-if="!plans"
            class="fas fa-spinner fa-spin"
        />
        <template v-for="(plan, key) in plans">
            <div
                :key="plan.id"
                class="plan-wrapper"
            >
                <div
                    class="plan"
                    :class="{active: activePlan === key }"
                    @click="activatePlan(key)"
                >
                    <div class="plan-container flex">
                        <div class="plan-icon">
                            <svg-vue class="check" icon="check" />
                        </div>
                        <div class="plan-credentials">
                            <div
                                class="plan-name"
                                :class="{'plan-with-pitch': plan.metadata.pitch}"
                            >
                                <span v-if="upgradePro || upgradeLifetime">Fusebox Pro</span> {{ plan.nickname }}
                            </div>
                            <div
                                v-if="!upgradePro || upgradeLifetime"
                                class="plan-price"
                            >
                                <template v-if="!coupon.type">
                                    {{ plan | billingPlanFilter }}/mo
                                </template>
                                <template v-else>
                                    <strike class="plan-discount">
                                        {{ plan | billingPlanFilter }}
                                    </strike>
                                    {{ discountPlan(plan) | billingPlanFilter }}/mo
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-if="plan.metadata.pitch"
                    class="plan-description"
                >
                    <template v-for="data in plan.metadata">
                        {{ data }}
                    </template>
                </div>
            </div>
        </template>
        <template v-if="plans">
            <div
                v-if=" 'year' === plans[activePlan].interval && !upgradePro && !upgradeLifetime"
                class="annual-plan-info"
            >
                You’ll be billed in one payment of
                <template v-if="!coupon.type">
                    {{ priceInUSD( plans[activePlan].amount ) }}
                </template>
                <template v-else>
                    {{ discountAnnualPlan( plans[activePlan] ) }}
                    </template>
                per year.
            </div>
        </template>
    </div>
</template>

<script>
import apiCall from '@/utils/apiCall';
import usdFormatter from '@/utils/usdFormatter';
import { find } from 'lodash';
import { mapGetters } from 'vuex';

export default {
    props: {
        upgradePro: {
            type: Boolean,
            default: false,
        },
        coupon: {
            type: Object,
            default: () => {},
        },
        upgradeLifetime: {
            type: Boolean,
            default: false,
        },
    },

    data: () => ({
        plans: null,
        activePlan: null,
    }),

    computed: {
        ...mapGetters(['userSubscriptionDetails']),
    },

    /**
     * Created hook
     *
     */
    async created() {
        this.getPlans();
    },

    methods: {
        /**
         * Get billing plans
         *
         */
        async getPlans() {
            const response = await apiCall(this, 'get', `${this.$appUrl}/api/billing/plans/pro`);

            const { status } = response;

            if (200 === status) {
                this.plans = response.data;
                const planIds = Object.keys(this.plans);

                this.activatePlan(planIds[0]);
                this.$emit('plan-changed', planIds[0]);
                this.$emit('plan-updated', this.plans[planIds[0]]);

                if (this.upgradePro) this.setUserPlan();

                if (this.upgradeLifetime) this.$event.$emit('planDetailsChange', this.plans[planIds[0]]);
            } else {
                this.$emit('subscription-plans-error');
            }
        },

        /**
         * Select active plan
         *
         * @param   {string}  id Plan id.
         */
        activatePlan(id) {
            this.activePlan = id;

            this.$emit('plan-changed', id);
            this.$emit('plan-updated', this.plans[id]);

            if (this.upgradeLifetime) this.$event.$emit('planDetailsChange', this.plans[id]);
        },

        /**
         * Calculates discount on plan
         *
         * @param   {object}  plan  Base plan.
         *
         * @returns  {string}        Calculated plan details.
         */
        discountPlan(plan) {
            return this[this.coupon.type](plan);
        },

        /**
         * Calculates discount in precent amount
         *
         * @param   {object}  plan  Base plan.
         *
         * @returns  {string}        Calculated plan details.
         */
        discountInPrecent(plan) {
            const newPlan = { ...plan };

            newPlan.amount = (plan.amount * ((100 - this.coupon.amount) / 100));

            return newPlan;
        },

        /**
         * Calculates discount in cash amount
         *
         * @param   {object}  plan  Base plan.
         *
         * @returns  {string}        Calculated plan details.
         */
        discountInCash(plan) {
            const newPlan = { ...plan };

            newPlan.amount = plan.amount - this.coupon.amount;

            return newPlan;
        },

        /**
         * Calculates full annual plan discount
         *
         * @param   {object}  plan  Base plan.
         *
         * @returns {string} Formatted discount annual plan.
         */
        discountAnnualPlan(plan) {
            const planWithDiscount = this[this.coupon.type](plan);

            return usdFormatter(planWithDiscount.amount);
        },

        /**
         * Formats price ins USD
         *
         * @param {number} amount Plan cost
         *
         * @returns {string} Formatted plan cost.
         */
        priceInUSD(amount) {
            return usdFormatter(amount);
        },

        /**
         * Sets active user plan
         *
         */
        setUserPlan() {
            this.plan = this.userSubscriptionDetails.subscriptionDetails.plan;

            // Checks if user is subscribed to a new plan
            const isNewPlan = find(this.plans, { id: this.plan.id });

            if (isNewPlan) {
                this.activatePlan(this.plan.id);
            } else {
                const newPlanEquivalent = find(this.plans, { interval: this.plan.interval });
                this.activatePlan(newPlanEquivalent.id);
            }
        },

    },
};
</script>
